import React, { useState, useRef, useEffect, useCallback } from 'react';

interface ProgressNumberInputProps {
  /** Committed value. 0 shows as an empty field, with the placeholder standing in. */
  value: number;
  onCommit: (value: number) => void;
  max?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
  title?: string;
  'aria-label'?: string;
}

const display = (value: number): string => (value === 0 ? '' : String(value));

/**
 * A number field that lets you empty it.
 *
 * Bound straight to a number, clearing the field commits 0, which immediately
 * renders back as "0" — so the digit reappears under the caret and whatever you
 * type next comes out with a leading zero you then have to delete. This keeps
 * the text you are typing in local state and only mirrors the committed value
 * back while the field is not focused, so an empty field stays empty.
 */
const ProgressNumberInput: React.FC<ProgressNumberInputProps> = ({
  value,
  onCommit,
  max,
  step = 1,
  className,
  disabled = false,
  title,
  'aria-label': ariaLabel,
}) => {
  const [text, setText] = useState(() => display(value));
  const focused = useRef(false);

  // Follow the committed value when the change came from somewhere else (a
  // template, a recompute); never while typing, or it would fight the caret.
  useEffect(() => {
    if (!focused.current) setText(display(value));
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    if (next === '') {
      onCommit(0);
      return;
    }
    const parsed = parseFloat(next);
    if (isNaN(parsed)) return; // mid-entry values like "-" or "." commit nothing
    const clamped = Math.min(Math.max(0, parsed), max ?? Infinity);
    onCommit(clamped);
  }, [onCommit, max]);

  return (
    <input
      type="number"
      className={className}
      aria-label={ariaLabel}
      disabled={disabled}
      title={title}
      value={text}
      placeholder="0"
      onFocus={() => { focused.current = true; }}
      onBlur={() => {
        focused.current = false;
        // Tidy up "007" or a half-typed "." once the user leaves the field.
        const parsed = parseFloat(text);
        setText(display(isNaN(parsed) ? 0 : Math.min(Math.max(0, parsed), max ?? Infinity)));
      }}
      onChange={handleChange}
      min={0}
      max={max}
      step={step}
    />
  );
};

export default ProgressNumberInput;
